// src/components/chat/MessageList.tsx
// Extracted message list component for better performance and maintainability
// Includes windowing: only renders recent messages for better performance with long chats

import React, { memo, useState, useCallback } from 'react';
import { IonIcon, IonSpinner, IonButton } from '@ionic/react';
import { send, checkmark, checkmarkDone, chevronUp } from 'ionicons/icons';
import { MessageReactions } from '../MessageReactions';
import {
  GameInvitationMessage,
  parseGameInvitation,
  stripInvitationMarker
} from '../games/crossroads/GameInvitationMessage';

const INITIAL_MESSAGE_COUNT = 50; // Only render last 50 messages initially
const LOAD_MORE_COUNT = 50; // Load 50 more each time

interface Message {
  id: string;
  content: string;
  sender_id: string;
  created_at: string;
  image_url?: string | null;
  read?: boolean;
}

interface MessageListProps {
  messages: Message[];
  loading: boolean;
  currentUserId: string;
  otherUserName: string;
  otherUserId: string;
  matchId: string;
  isPrideTheme: boolean;
  activeMessageId: string | null;
  messageReactions: { [messageId: string]: any[] };
  getMessageColor: (index: number) => { bg: string; text: string };
  formatTime: (timestamp: string) => string;
  onMessageClick: (messageId: string) => void;
  onImageClick: (e: any, imageUrl: string) => void;
  onAddReaction: (messageId: string, emoji: string) => void;
  onRemoveReaction: (messageId: string, emoji: string) => void;
}

export const MessageList: React.FC<MessageListProps> = memo(({
  messages,
  loading,
  currentUserId,
  otherUserName,
  otherUserId,
  matchId,
  isPrideTheme,
  activeMessageId,
  messageReactions,
  getMessageColor,
  formatTime,
  onMessageClick,
  onImageClick,
  onAddReaction,
  onRemoveReaction,
}) => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_MESSAGE_COUNT);

  const handleLoadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + LOAD_MORE_COUNT, messages.length));
  }, [messages.length]);

  // Reset visible count when messages change significantly (new chat opened)
  React.useEffect(() => {
    setVisibleCount(INITIAL_MESSAGE_COUNT);
  }, [messages.length === 0]);

  const hasMore = visibleCount < messages.length;
  const visibleMessages = messages.slice(-visibleCount); // Show most recent messages
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <IonSpinner />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        textAlign: 'center',
        padding: '40px 20px',
        color: 'var(--ion-color-medium)'
      }}>
        <IonIcon
          icon={send}
          style={{ fontSize: '60px', marginBottom: '15px', opacity: 0.5 }}
        />
        <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>No messages yet</h3>
        <p style={{ fontSize: '14px' }}>Say hi to {otherUserName}!</p>
      </div>
    );
  }

  return (
    <div className="messages-container" style={{ padding: '16px' }}>
      {hasMore && (
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <IonButton
            fill="clear"
            size="small"
            onClick={handleLoadMore}
            style={{ '--color': 'var(--ion-color-medium)' }}
          >
            <IonIcon icon={chevronUp} slot="start" />
            Load {Math.min(LOAD_MORE_COUNT, messages.length - visibleCount)} earlier messages
          </IonButton>
        </div>
      )}
      {visibleMessages.map((message, index) => {
        const isMe = message.sender_id === currentUserId;
        const isLastMessage = index === messages.length - 1;
        const showReadReceipt = isMe && isLastMessage && message.read;

        // Check if this is a game invitation
        const gameInvitation = parseGameInvitation(message.content);

        return (
          <div
            key={message.id}
            style={{
              display: 'flex',
              justifyContent: isMe ? 'flex-end' : 'flex-start',
              marginBottom: '12px',
            }}
          >
            <div
              className="message-bubble"
              onClick={() => onMessageClick(message.id)}
              style={{
                maxWidth: '70%',
                padding: message.image_url ? '6px' : '10px 14px',
                borderRadius: '18px',
                backgroundColor: isPrideTheme
                  ? getMessageColor(index).bg
                  : (isMe ? 'var(--ion-color-primary)' : 'var(--ion-color-secondary)'),
                color: isPrideTheme
                  ? getMessageColor(index).text
                  : (isMe ? 'var(--ion-color-primary-contrast)' : 'var(--ion-color-secondary-contrast)'),
                cursor: 'pointer',
              }}
            >
              {message.image_url && (
                <img
                  src={message.image_url}
                  alt="Shared image"
                  style={{
                    maxWidth: '250px',
                    maxHeight: '250px',
                    borderRadius: '12px',
                    display: 'block',
                    cursor: 'pointer',
                    objectFit: 'cover'
                  }}
                  onClick={(e) => onImageClick(e, message.image_url!)}
                />
              )}
              {message.content && message.content !== '[Image]' && (
                gameInvitation ? (
                  <GameInvitationMessage
                    invitationData={gameInvitation}
                    senderId={message.sender_id}
                    senderName={isMe ? 'You' : otherUserName}
                    isOwnMessage={isMe}
                    matchId={matchId}
                  />
                ) : (
                  <div style={{
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    padding: message.image_url ? '8px' : '0',
                    paddingTop: message.image_url ? '6px' : '0'
                  }}>
                    {stripInvitationMarker(message.content)}
                  </div>
                )
              )}
              <div
                style={{
                  fontSize: '13px',
                  marginTop: '4px',
                  opacity: 0.7,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  justifyContent: 'flex-end',
                  padding: message.image_url ? '0 8px 2px 8px' : '0'
                }}
              >
                <span>{formatTime(message.created_at)}</span>
                {showReadReceipt && (
                  <IonIcon
                    icon={message.read ? checkmarkDone : checkmark}
                    style={{ fontSize: '14px' }}
                  />
                )}
              </div>

              {/* Emoji Reactions */}
              <MessageReactions
                reactions={messageReactions[message.id] || []}
                currentUserId={currentUserId}
                onAddReaction={(emoji) => onAddReaction(message.id, emoji)}
                onRemoveReaction={(emoji) => onRemoveReaction(message.id, emoji)}
                isActive={activeMessageId === message.id}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
});

MessageList.displayName = 'MessageList';
